/**
 * LVMH specific configuration.
 *
 * This will be merged with `src/config/index.ts`
 *
 */
import customerDataForm from "./forms/customerData";
import customerConsentForm from "./forms/customerConsent";
import customerAdditionalInfo from "./forms/customerAdditionalInfo";
import advisorForm from "./forms/advisorForm";
import debug from "../../utils/debug";

const supportedLanguages = ["en", "fr", "ja", "zh-Hant", "zh-Hans", "ko"];

const stage: string = process.env.REACT_APP_STAGE || "dev";
const [OIDC_BASE_URL, OIDC_CLIENT_ID, API_BASE_URL, DQE_ENDPOINT] =
  stage === "local"
    ? [
        "http://localhost:5000",
        "0oa34v66cteoxWA5C417",
        "http://localhost:5000",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=M90MnyS6.ZXlUVenznBGhc00bg&Adresse={{search}}"
      ]
    : stage === "dev"
    ? [
        "https://wj.okta.com/oauth2/aus34v6fymmJnKlXL417",
        "0oa34v66cteoxWA5C417",
        "https://apis.dev.fred.clienteling.lvmhda.com",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=M90MnyS6.ZXlUVenznBGhc00bg&Adresse={{search}}"
      ]
    : stage === "staging"
    ? [
        "https://wj.okta.com/oauth2/aus38i0iplqjILPCt417",
        "0oa38hwchsABpK8dA417",
        "https://apis.staging.fred.clienteling.lvmhda.com",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=M90MnyS6.ZXlUVenznBGhc00bg&Adresse={{search}}"
      ]
    : [
        "https://wj.okta.com/oauth2/aus38i2g5m41mLjIz417",
        "0oa38i1vdhD5kzLrM417",
        "https://apis-b.fred.clienteling.lvmhda.com",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=M90MnyS6.ZXlUVenznBGhc00bg&Adresse={{search}}"
      ];
debug("env:", stage);
debug("baseUrl:", OIDC_BASE_URL);

export default {
  id: "fred",
  languages: {
    supportedLanguages
  },
  customise: {
    customer: {
      DQEValidationPhone:
        "https://prod2.dqe-software.com/TEL/?Tel=" +
        "{!phone}" +
        "&Licence=M90MnyS6.ZXlUVenznBGhc00bg&Pays=" +
        "{!currentCountry}",
      enableAdditionalInfo: false,
      enableAdvisorForm: false
    },
    fields: {
      markRequiredWithSymbol: "*",
      customFields: [
        "maritalStatus",
        "weddingDate",
        "preferredContactChannel",
        "instantMessageType",
        "instantMessageDescription",
        "clientPotential",
        "children",
        "upcomingEvent",
        "beverage",
        "doNotServe",
        "favoriteCollection",
        "category",
        "material",
        "pavedStacking",
        "pet",
        "invitationType"
      ]
    },
    verifySalesPerson: {
      enable: true,
      capitaliseId: true,
      resetOnActions: true
    }
  },
  zones: {
    worldwide: {
      languages: supportedLanguages,
      supportedLanguages
    },
    europe: {
      languages: ["en", "fr"],
      supportedLanguages: ["en", "fr"]
    },
    africa: {
      languages: ["en", "fr"],
      supportedLanguages: ["en", "fr"]
    },
    asia: {
      languages: ["ja", "zh-Hans", "zh-Hant", "ko"],
      supportedLanguages: ["en", "ja", "zh-Hans", "zh-Hant"]
    },
    jp: {
      languages: ["ja"],
      supportedLanguages: ["en", "ja"]
    },
    apac: {
      languages: ["ja", "zh-Hans", "zh-Hant"],
      supportedLanguages: ["en", "ja", "zh-Hans", "zh-Hant"]
    },
    americas: {
      languages: ["en", "es"],
      supportedLanguages: ["en", "es"]
    }
  },
  services: {
    oidc: {
      baseUrl: OIDC_BASE_URL,
      loginRedirectEndpoint: "/v1/authorize",
      scope: "openid offline_access",
      getTokenEndpoint: "/v1/token",
      logoutRedirectEndpoint: "/v1/logout",
      clientId: OIDC_CLIENT_ID,
      codeChallenge: "HPyLLwWqLbWJV0Ks7fArAD1TEGtV6JI2P8aG1EaVdjY",
      codeChallengeMethod: "S256",
      codeVerifier:
        "swbBEq~75oJQVAdgJEND6n7HNf5seQJIOuqwNE8qt33JOIbAYzsqOY.YmV_~1l_.nViF4f0xSiy87IsMh52zkHXwigneHj5FP2wcgUC0te~imSlRXsYrf4j1fH9_MwqJ"
    },
    api: {
      baseUrl: API_BASE_URL,
      getConfigEndpoint: "/ddc/v1/ddcconfig",
      getStoresEndpoint: "/ddc/v1/stores",
      verifyAdvisorEndpoint: "/advisors/v1/advisors",
      getTitlesEndpoint: "/ddc/v1/titles",
      getCountriesEndpoint: "/ddc/v1/countries",
      translationsEndpoint: "/ddc/v1/translations/{{lng}}/{{ns}}",
      customerEndpoint: "/clients/v1/clients",
      dqeAddressesEndpoint: DQE_ENDPOINT,
      dqeEmailEndpoint: `https://prod2.dqe-software.com/DQEEMAILLOOKUP/?Email={{email}}&Licence=M90MnyS6.ZXlUVenznBGhc00bg`
    },
    googleAnalytics: {
      options: {},
      customise: {
        dimensions: {
          traceToken: "dimension1",
          salesPersonId: "dimension2",
          storeId: "dimension3",
          storeCountry: "dimension4",
          attempt: "dimension5",
          requestId: "dimension6",
          storeName: "dimension7",
          countryName: "dimension8"
        },
        metrics: {}
      }
    }
  },
  forms: {
    schema: "form/customerFormDefinitions.json",
    fields: [
      "form/customerDataFields.json",
      "form/customerConsentFields.json",
      "form/customerThanks.json",
      "form/additionalInfoFields.json"
    ],
    customer: [
      customerDataForm,
      customerConsentForm,
      customerAdditionalInfo,
      advisorForm
    ]
  }
};
